import React, { useState, useEffect, useRef } from "react";
import PageTitle from "../../../Module/pageTitle";
import Dropdown from "../../../Component/dropdown";
import { useDispatch } from "react-redux";
import { getPYQanalysis, getSubExam, updatePyqpaperAnalysis } from "../../../Action/adminPanelAction";
import {
  getExamListNew,
  getParentCourseNew,
} from "../../../Action/courseAction";
import "./pyqAnalyticsDashboard.css";
import "./knowledgeBaseUpload.css";
import { Upload, FileText, Play, Logs, Zap, Wand2, Trash2 } from "lucide-react";
import {
  addPYQAnalysis,
  addStoreKnowledgeBaseAIData,
  getAIPrompt,
  getAISimplePrompt,
  uploadAIFiles,
  uploadAiJsonFiles,
} from "../../../Action/adminPanelAction";
import ReactMarkdown from "react-markdown";
import * as pdfjs from "pdfjs-dist";
import { monthData, yearData, yearData1 } from "../../../Utils/dataConstants";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const supportedFormats = ["PDF"];
// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS (module-level, never re-created)
// ─────────────────────────────────────────────────────────────────────────────
const DIFF_COLORS = {
  easy: "#34d399",
  medium: "#fbbf24",
  hard: "#fb7185",
};

const COG_COLORS = ["#22d3ee", "#4f7cff", "#a78bfa", "#f472b6"];

const Q_COLORS = ["#4f7cff", "#a78bfa", "#34d399", "#fbbf24", "#fb7185"];

const VISUAL_ICONS = {
  Graphs: "📈",
  "Diagrams (biological structures, pedigrees, gel images)": "🧬",
  Tables: "📊",
  "Flowcharts / Pathway schematics": "🔄",
};

const KT_CLASS = {
  HYBRID: "badge-hybrid",
  CONCEPTUAL: "badge-conceptual",
  STATIC: "badge-static",
  APPLICATION: "badge-application",
};

const PYQ_CLASS = {
  HIGH: "badge-high",
  MEDIUM: "badge-medium",
  LOW: "badge-low",
};

// ─────────────────────────────────────────────────────────────────────────────
// HOOK — must be defined outside any component that calls it conditionally
// ─────────────────────────────────────────────────────────────────────────────
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS — defined at module level, never inside render
// ─────────────────────────────────────────────────────────────────────────────
function AnimatedBar({ label, value, color, maxVal = 100, delay = 0 }) {
  const [ref, visible] = useReveal();
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => setWidth((value / maxVal) * 100), delay);
    return () => clearTimeout(t);
  }, [visible, value, maxVal, delay]);

  return (
    <div className="bar-row" ref={ref}>
      <span className="bar-label">{label}</span>
      <div className="bar-track">
        <div
          className="bar-fill"
          style={{
            width: `${width}%`,
            background: `linear-gradient(90deg, ${color}, ${color}88)`,
          }}
        />
      </div>
      <span className="bar-pct">{value}%</span>
    </div>
  );
}

function DonutChart({ data, colors, size = 120 }) {
  const [ref, visible] = useReveal();
  const total = data?.reduce((s, d) => s + d.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.38;
  const stroke = size * 0.1;
  const circ = 2 * Math.PI * r;
  let offset = 0;

  const slices = data?.map((d, i) => {
    const frac = d.value / total;
    const dash = frac * circ;
    const gap = circ - dash;
    const rot = (offset / total) * 360 - 90;
    offset += d.value;
    return { ...d, dash, gap, rot, color: colors[i % colors.length] };
  });

  return (
    <div className="donut-container" ref={ref}>
      <svg
        className="donut-svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        {slices?.map((s, i) => (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={stroke}
            strokeDasharray={`${visible ? s.dash : 0} ${circ}`}
            strokeDashoffset={0}
            transform={`rotate(${s.rot} ${cx} ${cy})`}
            style={{
              transition: `stroke-dasharray 1.2s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s`,
              opacity: visible ? 1 : 0,
            }}
          />
        ))}
        <circle cx={cx} cy={cy} r={r - stroke / 2 - 2} fill="rgba(0,0,0,0.3)" />
      </svg>
      <div className="donut-legend">
        {slices?.map((s, i) => (
          <div className="legend-item" key={i}>
            <div className="legend-dot" style={{ background: s.color }} />
            <span className="legend-name">{s.label}</span>
            <span className="legend-val">{s.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, delay }) {
  const [ref, visible] = useReveal();
  return (
    <div
      className={`stat-card ${visible ? "animated" : ""}`}
      ref={ref}
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function UnitRow({ unit, delay }) {
  const [ref, visible] = useReveal();
  const [open, setOpen] = useState(false);
  const [barW, setBarW] = useState(0);
  const maxWeight = 20;

  const ktClass = KT_CLASS[unit.knowledge_type] || "badge-hybrid";
  const pyqClass = PYQ_CLASS[unit.pyq_sufficiency] || "badge-medium";

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(
      () => setBarW((unit.weightage / maxWeight) * 100),
      delay * 1000 + 400,
    );
    return () => clearTimeout(t);
  }, [visible, unit.weightage, delay]);

  return (
    <div>
      <div
        className={`unit-row ${visible ? "animated" : ""} ${open ? "expanded" : ""}`}
        ref={ref}
        style={{ animationDelay: `${delay}s` }}
        onClick={() => setOpen((o) => !o)}
      >
        <div className="unit-info">
          <div className="unit-name">{unit.unit_name}</div>
          <div className="unit-badges" style={{ marginTop: 8 }}>
            <span className={`badge ${ktClass}`}>{unit.knowledge_type}</span>
            <span className={`badge ${pyqClass}`}>
              PYQ: {unit.pyq_sufficiency}
            </span>
            {unit.outside_knowledge_required && (
              <span
                className="badge"
                style={{
                  background: "rgba(251,113,133,0.08)",
                  color: "#fb7185",
                  border: "1px solid rgba(251,113,133,0.2)",
                }}
              >
                +Outside
              </span>
            )}
          </div>
        </div>
        <div className="unit-weight">
          <span className="weight-num">{unit.weightage}%</span>
          <div className="weight-bar-mini">
            <div className="weight-bar-fill" style={{ width: `${barW}%` }} />
          </div>
        </div>
        <span className="chevron">▾</span>
      </div>

      {unit.topics && unit.topics.length > 0 && (
        <div
          className={`topics-panel ${open ? "open" : ""}`}
          style={{
            borderRadius: "0 0 10px 10px",
            border: "1px solid rgba(99,140,255,0.12)",
            borderTop: "none",
          }}
        >
          <div className="topics-inner">
            {unit?.topics?.map((t, i) => {
              const tKtClass = KT_CLASS[t.knowledge_type] || "badge-hybrid";
              const tPyqClass = PYQ_CLASS[t.pyq_sufficiency] || "badge-medium";
              return (
                <div className="topic-item" key={i}>
                  <div className="topic-dot" />
                  <span className="topic-name">{t.topic_name}</span>
                  <div className="topic-badges">
                    <span className={`badge ${tKtClass}`}>
                      {t.knowledge_type}
                    </span>
                    <span className={`badge ${tPyqClass}`}>
                      {t.pyq_sufficiency}
                    </span>
                    <span
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: 11,
                        color: "#4f7cff",
                        fontWeight: 600,
                      }}
                    >
                      {t.weightage}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function InsightCard({ text, index }) {
  const [ref, visible] = useReveal();
  return (
    <div
      className={`insight-card ${visible ? "animated" : ""}`}
      ref={ref}
      style={{ animationDelay: `${index * 0.07}s` }}
    >
      <span className="insight-num">{String(index + 1).padStart(2, "0")}</span>
      <p className="insight-text">{text}</p>
    </div>
  );
}

function SectionTitle({ children }) {
  const [ref, visible] = useReveal();
  return (
    <div
      className="section-title"
      ref={ref}
      style={{ opacity: visible ? 1 : 0, transition: "opacity 0.5s ease" }}
    >
      {children}
    </div>
  );
}

function DistCard({ icon, title, iconBg, animDelay, children }) {
  const [ref, visible] = useReveal();
  return (
    <div
      className={`dist-card ${visible ? "animated" : ""}`}
      ref={ref}
      style={{ animationDelay: animDelay }}
    >
      <div className="dist-card-title">
        <div className="dist-card-title-icon" style={{ background: iconBg }}>
          {icon}
        </div>
        {title}
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const PyqAnalyticsDashboard = () => {
  const dispatch = useDispatch();

  const [exam, setExam] = useState("");
  const [subExam, setSubExam] = useState("");
  const [examStream, setExamStream] = useState("");
  const [examList, setExamList] = useState([]);
  const [subExamList, setSubExamList] = useState([]);
  const [examStreamList, setExamStreamList] = useState([]);
  const [parsed, setParsed] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [editExam, setEditExam] = useState("");
  const [editSubExam, setEditSubExam] = useState("");
  const [editExamStream, setEditExamStream] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const [editExamList, setEditExamList] = useState([]);
  const [editsubExamList, setEditSubExamList] = useState([]);
  const [editExamStreamList, setEditExamStreamList] = useState([]);
  //   kjhjkdskk

  useEffect(() => {
    dispatch(
      getExamListNew((res) => {
        setExamList(res);
        setEditExamList(res);
      }),
    );
  }, []);

  const onChangeExam = (value) => {
    setExam(value);
    setSubExam("");
    setSubExamList([]);
    setExamStream("");
    setExamStreamList([]);
    setParsed(null);

    if (value) {
      dispatch(
        getSubExam({ examTypeId: value._id }, (res) => {
          if (res.data.length > 0) {
            setSubExamList(res.data);
            setEditSubExamList(res.data);
          } else {
            dispatch(
              getParentCourseNew(`${value._id}`, (r) => {
                setExamStreamList(r);
                setEditExamStreamList(r);
              }),
            );
          }
        }),
      );
    }
  };

  const onChangeSubExam = (value) => {
    setSubExam(value);
    setExamStream("");
    setParsed(null);

    if (value) {
      dispatch(
        getParentCourseNew(`${exam._id}/${value._id}`, (res) => {
          setExamStreamList(res);
          setEditExamStreamList(res);
        }),
      );
    }
  };

  const onChangeExamStream = (value) => {
    setExamStream(value);
    setParsed(null);

    if (value?._id) {
      const examObj = { examTypeId: exam._id, baseCourseId: value._id };
      if (subExam) examObj.subExamTypeId = subExam._id;

      dispatch(
        getPYQanalysis(
          examObj,
          (res) => {
            if (res && res.length > 0) {
              setParsed(res[0]); 
            } else {
              setParsed(null);
            }
          },
          () => {},
        ),
      );
    }
  };

  // Derived data — safe because parsed is either null or a valid object
  const bp = parsed?.exam_blueprint ?? null;
  const pyq = parsed?.pyq_intelligence ?? null;

  const diffData = bp?.difficulty_distribution
    ? Object.entries(bp.difficulty_distribution)?.map(([k, v]) => ({
        label: k.charAt(0).toUpperCase() + k.slice(1),
        value: v,
      }))
    : [];

  const cogData = bp?.cognitive_distribution
    ? Object.entries(bp.cognitive_distribution)?.map(([k, v]) => ({
        label: k.charAt(0).toUpperCase() + k.slice(1),
        value: v,
      }))
    : [];
const handleEditMode = () => {
      setIsEditMode((prev) => !prev);
};
  // khjk


  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [uploadFiles, setUploadFiles] = useState(false);
  const [initialPromptPayload, setInitialPromptPayload] = useState("");
  const [nextPromptPayload, setNextPromptPayload] = useState("");
  const [responseFromPrompt, setResponseFromPrompt] = useState("");
  const [command, setCommand] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [extractedJSON, setExtractedJSON] = useState("");
useEffect(() => {
  if (isEditMode && parsed) {
    setEditExam(exam);
    setEditSubExam(subExam);
    setEditExamStream(examStream);
  }
}, [isEditMode,parsed]);
  const onChangeExamEdit = (value) => {
    setEditExam(value);
    setEditSubExam("");
    setEditExamStream("");
    setEditSubExamList([]);
    setEditExamStreamList([]);
    if (value) {
      dispatch(
        getSubExam({ examTypeId: value._id }, (res) => {
          if (res.data.length > 0) {
            setEditSubExamList(res.data);
          } else {
            dispatch(
              getParentCourseNew(`${value._id}`, (res) => {
                setEditExamStreamList(res);
              }),
            );
          }
        }),
      );
    }
  };

  const onChangeSubExamEdit = (value) => {
    setEditSubExam(value);
    setEditExamStream("");
    setEditExamStreamList([]);
    if (value) {
      dispatch(
        getParentCourseNew(`${editExam._id}/${value._id}`, (res) => {
          setEditExamStreamList(res);
        }),
      );
    }
  };

  const onChangeExamStreamEdit = (value) => {
    setEditExamStream(value);
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      formUploadPayload(fileArray);
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      formUploadPayload(fileArray);
    }
  };

  const formUploadPayload = (fileArray) => {
    const newFiles = fileArray?.map((file) => {
      let fileType = "other";

      if (file.type.startsWith("image/")) {
        fileType = "image";
      } else if (file.type === "application/pdf") {
        fileType = "document";
      } else {
        fileType = "other";
      }

      return {
        month: month || "",
        year: year || "",
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2),
        fileType: fileType,
        file: file,
        type: file.type,
        progress: 0,
        timestamp: new Date().toLocaleString(),
      };
    });

    setUploadedFiles((prev) => [...prev, ...newFiles]);

    // setMonth("");
    // setYear("");
    setUploadFiles(false);
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  const uploadFilePromise = (file) => {
    return new Promise((resolve, reject) => {
      dispatch(
        uploadAIFiles(file, (res) => {
          if (res) {
            resolve(res);
          } else {
            reject("Upload failed");
          }
        }),
      );
    });
  };

  const handleUplodFiles = async () => {
    setUploadFiles(true);

    for (let index = 0; index < uploadedFiles.length; index++) {
      const item = uploadedFiles[index];
      if (item?.fileType === "image" || item?.fileType === "document") {
        // 🔄 set progress 50
        setUploadedFiles((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            progress: 50,
          };
          return updated;
        });

        try {
          const res = await uploadFilePromise(item?.file);

          // ✅ update after upload complete
          setUploadedFiles((prev) => {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              fileId: res.fileId,
              fileName: res.fileName,
              progress: 100,
            };
            return updated;
          });
        } catch (err) {
          // ❌ handle error
          setUploadedFiles((prev) => {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              progress: 0,
              error: true,
            };
            return updated;
          });
          setUploadFiles(false);
        }
      }
    }
  };

  const extractJSON = (text) => {
    if (!text) return null;

    let start = text.indexOf("{");
    let end = text.lastIndexOf("}");

    while (start !== -1 && end !== -1 && start < end) {
      const possibleJSON = text.slice(start, end + 1);

      try {
        return JSON.parse(possibleJSON);
      } catch (e) {
        // move start forward to next {
        start = text.indexOf("{", start + 1);
      }
    }

    return null;
  };
  const handleStartExtraction = async () => {
    let newArray = [...uploadedFiles];

    let urls = newArray
      ?.map((item) => {
        if (item?.fileType === "image") {
          return { type: item?.fileType, source: { type: "file", file_id: item?.fileId } };
        }
        if (item?.fileType === "document") {
          return { type: item?.fileType, source: { type: "file", file_id: item?.fileId } };
        }
        if (item?.fileType === "text") {
          return { type: item?.fileType, text: item?.content };
        }
        if (item?.fileType === "url" && (item?.type === "image" || item?.type === "document")) {
          return { type: item?.type, source: item?.source };
        }
        if (item?.fileType === "url" && item?.type === "website") {
          return { type: "text", text: item?.source?.url };
        }
        return null;
      })
      .filter(Boolean);

    // ─── Strip MongoDB _id fields from existing data before sending to AI ───
    const cleanExistingData = JSON.parse(
      JSON.stringify(parsed, (key, value) =>
        key === "_id" || key === "__v" || key === "createdAt" || key === "updatedAt" ? undefined : value
      )
    );

    const DefaultPrompt = `You are an expert in exam analytics and PYQ intelligence extraction.

You will receive TWO inputs:
1. EXISTING_DATA — the already-saved exam blueprint JSON (provided below)
2. NEW_PDF — a new set of Previous Year Questions uploaded by the user

---

## YOUR ONLY JOB

Analyze the NEW_PDF and find topics and sub-topics that DO NOT already exist in EXISTING_DATA.

Then return a MERGED JSON that is:
- The EXISTING_DATA copied 100% exactly as-is (every field, every value, every array item)
- PLUS only the genuinely new topics/sub-topics appended into the correct unit
- PLUS new pattern_insights and generation_rules appended (do NOT remove old ones)
- PLUS updated total_questions count (add new questions found to existing count)
- PLUS updated unit weightage percentages (recalculate based on new total)

---

## STRICT MATCHING RULES

Before adding any topic, check against EXISTING_DATA:
- If a topic with the same or very similar name already exists in that unit → DO NOT add it again
- If a sub-topic already exists under a topic → DO NOT add it again
- Only add if genuinely new concept not covered by any existing topic/sub-topic
- Similarity check: treat as duplicate if >80% conceptually same even if wording differs
  Example: "Simple Interest" and "SI Calculations" = SAME → skip

---

## FOR EACH NEW TOPIC FOUND, generate this full structure:

{
  "topic_name": "",
  "weightage": number,
  "knowledge_type": "STATIC | CONCEPTUAL | DYNAMIC | HYBRID",
  "pyq_sufficiency": "HIGH | MEDIUM | LOW",
  "outside_knowledge_required": false,
  "outside_type": [],
  "visual_distribution_weightage": 0,

  "bdf": {
    "easy_percentage": number,
    "medium_percentage": number,
    "hard_percentage": number,
    "difficulty_trend": "increasing | stable | decreasing",
    "hardest_sub_topic": "",
    "easiest_sub_topic": "",
    "difficulty_discriminator": "",
    "bdf_score": number
  },

  "year_wise_frequency": {
    "data": [ { "year": "", "count": number, "percentage": number } ],
    "frequency_trend": "rising | falling | stable | volatile | unknown",
    "peak_year": "",
    "last_seen_year": "",
    "consecutive_years_present": number,
    "avg_questions_per_year": number
  },

  "topic_traps": [
    {
      "trap_name": "",
      "trap_description": "",
      "frequency": "high | medium | low"
    }
  ],

  "sub_topics": [
    {
      "sub_topic_name": "",
      "weightage": number,
      "knowledge_type": "",
      "pyq_sufficiency": "",
      "bdf_score": number,
      "difficulty_trend": "increasing | stable | decreasing",
      "frequently_tested_concept": "",
      "outside_knowledge_required": false,
      "outside_type": []
    }
  ]
}

---

## FOR EACH NEW SUB-TOPIC (under an existing topic), generate:

{
  "sub_topic_name": "",
  "weightage": number,
  "knowledge_type": "",
  "pyq_sufficiency": "",
  "bdf_score": number,
  "difficulty_trend": "increasing | stable | decreasing",
  "frequently_tested_concept": "",
  "outside_knowledge_required": false,
  "outside_type": []
}

Append it to the sub_topics array of the matching existing topic.
If the existing topic has no sub_topics field yet, create it as an array.

---

## WEIGHTAGE RECALCULATION RULES

After appending new topics:
- Recalculate topic weightages within each unit so they still sum to 100
- Recalculate unit weightages so they still sum to 100
- Adjust existing topic weightages proportionally — do NOT zero them out
- Formula: new_weightage = (old_count / new_total_count) * 100

---

## EXISTING_DATA (your base — copy this entirely, only append to it):

${JSON.stringify(cleanExistingData, null, 2)}

---

## OUTPUT RULES

- Return ONLY the final merged JSON — nothing else
- NO markdown, NO backticks, NO explanation outside JSON
- The output must be valid parseable JSON
- Every existing field must be present in output exactly as given
- Do NOT rename, reorder, or remove any existing field
- Do NOT add fields that don't exist in the schema above
- New topics go at the END of the topics array of their unit
- New sub-topics go at the END of the sub_topics array of their topic
- If zero new topics/sub-topics found → return EXISTING_DATA unchanged
- If year data unavailable from new PDF → set data: [], frequency_trend: "unknown"`;

    const postData = {
      systemPrompt: [
        {
          text: DefaultPrompt,
          type: "text",
          cache_control: { type: "ephemeral" },
        },
      ],
      prompt: urls,
    };

    setInitialPromptPayload(postData?.prompt);

    const result = await new Promise((resolve, reject) => {
      dispatch(
        getAISimplePrompt(postData, (res) => {
          const rawText =
            res?.content?.[0]?.text ||
            res?.data?.output ||
            res?.output ||
            res?.message ||
            res ||
            "";

          setResponseFromPrompt(rawText);

          try {
            const cleaned = rawText
              .replace(/^```json\s*/i, "")
              .replace(/^```\s*/i, "")
              .replace(/```\s*$/i, "")
              .trim();

            const extractJSONData = extractJSON(cleaned);

            // ─── Reattach MongoDB IDs from existing data ───
            if (parsed?._id) extractJSONData["_id"] = parsed._id;
            extractJSONData["examTypeId"] = editExam?._id;
            extractJSONData["baseCourseId"] = editExamStream?._id;
            if (editSubExam) {
              extractJSONData["subExamTypeId"] = editSubExam?._id;
            }

            dispatch(updatePyqpaperAnalysis(parsed?._id,extractJSONData, (res) => {
                console.log('updatePyqpaperAnalysis', res)
            }));
            setExtractedJSON(extractJSONData);
            resolve(extractJSONData);
          } catch (err) {
            reject(err);
          }
        })
      );
    });
  };


  return (
    <div className="contentBox">
      <PageTitle
        name="PYQ Knowledge Base — Exam Analytics"
        isEditMode={!!parsed}
        btnName={isEditMode ? "Back" : "Edit"}
        onClick={handleEditMode}
      />
      <div
        className="formDiv contentScrollbar"
        style={{ background: "#f4f6fb" }}
      >
        {isEditMode  ? (
          <div className="knowledge-upload-container">
            {/* Main Content */}
            <div className="upload-content d-flex justify-content-center align-items-center">
              {/* Left Section - Upload */}
              <div className="upload-section col-md-7">
                {/* Upload Materials Card */}
                <div className="upload-card">
                  <div className="card-header-KB">
                    <FileText size={20} />
                    <h2>Upload Materials</h2>
                    <button className="format-btn">All Formats</button>
                  </div>

                  <div className="upload-form">
                    <div className="row">
                      {/* Exam Selection */}
                      <div className="col-md-6 form-group">
                        <label htmlFor="exam">EXAM *</label>
                        <Dropdown
                          labelName="Select Exam Name"
                          setInputText={(value) => onChangeExamEdit(value)}
                          options={editExamList}
                          value={editExam}
                        />
                      </div>

                      {/* Sub Exam Selection */}
                      {editsubExamList?.length > 0 ? (
                        <div className="col-md-6 form-group">
                          <label htmlFor="sub exam">SUB EXAM *</label>
                          <Dropdown
                            labelName="Select Sub Exam"
                            setInputText={(value) => onChangeSubExamEdit(value)}
                            options={editsubExamList}
                            value={editSubExam}
                          />
                        </div>
                      ) : null}

                      {/* Exam Stream Selection */}
                      <div className="col-md-6 form-group">
                        <label htmlFor="exam stream">EXAM STREAM *</label>
                        <Dropdown
                          labelName="Select Exam Stream"
                          setInputText={(value) =>
                            onChangeExamStreamEdit(value)
                          }
                          options={editExamStreamList}
                          value={editExamStream}
                        />
                      </div>
                    </div>

                    <div className="row">
                      {/* Exam Selection */}
                      <div className="col-md-6 form-group">
                        <label htmlFor="exam">MONTH *</label>
                        <Dropdown
                          labelName="Select Month"
                          setInputText={(value) => setMonth(value)}
                          options={monthData}
                          value={month}
                        />
                      </div>

                      {/* Exam Stream Selection */}
                      <div className="col-md-6 form-group">
                        <label htmlFor="exam stream">YEAR *</label>
                        <Dropdown
                          labelName="Select Year"
                          setInputText={(value) => setYear(value)}
                          options={yearData1}
                          value={year}
                        />
                      </div>
                    </div>

                    {/* Drag and Drop Area */}
                    <label
                      className={`drag-drop-area ${dragActive ? "active" : ""}`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <Upload size={48} className="upload-icon" />
                      <h3>Drop files here or click to browse</h3>
                      <p>Supports all major formats</p>
                      <div className="format-badges">
                        {supportedFormats?.map((format) => (
                          <span key={format} className="format-badge">
                            {format}
                          </span>
                        ))}
                      </div>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => handleFileInput(e)}
                        className="file-input"
                        accept=".pdf"
                      />
                    </label>
                  </div>

                  {/* Upload Queue */}
                {uploadedFiles?.length > 0 &&  <div className="upload-queue">
                    <h3>Upload Queue</h3>
                    <div className="queue-count">
                      {uploadedFiles.length} files
                    </div>

                    {uploadedFiles.length === 0 ? (
                      <div className="no-files">No files uploaded yet</div>
                    ) : (
                      <div className="files-list">
                        {uploadedFiles?.map((file, index) => (
                          <div key={index} className="file-item">
                            <div className="file-row">
                              <div className="file-info">
                                <FileText size={16} />
                                <div className="file-details">
                                  <p className="file-name fw-bold">
                                    {`${file.month?.name} - ${file.year?._id}`}
                                  </p>
                                  <p className="file-name">{file.name}</p>
                                  <p className="file-meta mb-0">
                                    {file.size} MB • {file.timestamp}
                                  </p>
                                </div>
                              </div>
                              <button
                                className="remove-btn"
                                onClick={() => handleRemoveFile(index)}
                                title="Remove file"
                              >
                                ✕
                              </button>
                            </div>
                            <div className="file-progress">
                              <div
                                className="progress"
                                role="progressbar"
                                // aria-label="Example 1px high"
                                aria-valuenow={file.progress || 0}
                                aria-valuemin="0"
                                aria-valuemax="100"
                                style={{ height: "2px" }}
                              >
                                <div
                                  className="progress-bar"
                                  style={{ width: `${file.progress || 0}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>}
                  <div className="config-form">
                    {/* Start Button */}
                    {uploadFiles ? (
                      <button
                        className="start-btn"
                        onClick={handleStartExtraction}
                      >
                        <Zap size={18} />
                        <span>Execute AI Command</span>
                      </button>
                    ) : (
                      <button className="start-btn" onClick={handleUplodFiles}>
                        <Upload size={18} />
                        <span>Upload Files</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {/* ── FILTERS ── */}
            <div className="row mx-0 mt-2">
              <div className="col-md-4 form-group">
                <label>EXAM *</label>
                <Dropdown
                  labelName="Select Exam Name"
                  setInputText={onChangeExam}
                  options={examList}
                  value={exam}
                />
              </div>

              {subExamList.length > 0 && (
                <div className="col-md-4 form-group">
                  <label>SUB EXAM *</label>
                  <Dropdown
                    labelName="Select Sub Exam"
                    setInputText={onChangeSubExam}
                    options={subExamList}
                    value={subExam}
                  />
                </div>
              )}

              <div className="col-md-4 form-group">
                <label>EXAM STREAM *</label>
                <Dropdown
                  labelName="Select Exam Stream"
                  setInputText={onChangeExamStream}
                  options={examStreamList}
                  value={examStream}
                />
              </div>
            </div>

            {/* ── DASHBOARD ── */}
            {parsed && bp && pyq ? (
              <>
                <div className="orb orb-1" />
                <div className="orb orb-2" />
                <div className="app-wrapper">
                  {/* HEADER */}
                  <div className="pyq-header">
                    <div className="pyq-header-glow" />
                    <div className=" d-flex justify-content-between align-items-center">
                      <div className="pyq-header-badge">
                        <span className="pyq-header-badge-dot" />
                        Exam Intelligence Dashboard
                      </div>
                      <div className="pyq-header-meta">
                        <div className="meta-chip">
                          <strong>{parsed.total_questions}</strong> Questions
                        </div>
                        <div className="meta-sep" />
                        <div className="meta-chip">
                          <strong>{pyq.units?.length}</strong> Units
                        </div>
                        <div className="meta-sep" />
                        <div className="meta-chip">
                          <strong>{bp.visual_distribution?.percentage}%</strong>{" "}
                          Visual Content
                        </div>
                      </div>
                    </div>

                    <h1>
                      {parsed.exam?.split(" ").slice(0, 2).join(" ")}{" "}
                      <em>{parsed.exam?.split(" ").slice(2).join(" ")}</em>
                    </h1>

                    {/* <div className="pyq-header-divider" /> */}
                  </div>

                  {/* STATS */}
                  <div className="stats-row">
                    <StatCard
                      label="Total Questions"
                      value={parsed?.total_questions||0}
                      sub="Part A + B + C"
                      delay={0.1}
                    />
                    <StatCard
                      label="Units Covered"
                      value={pyq?.units?.length||0}
                      sub="Across all sections"
                      delay={0.15}
                    />
                    <StatCard
                      label="Visual Content"
                      value={`${bp?.visual_distribution?.percentage ?? 0}%`}
                      sub="Graphs, diagrams, tables"
                      delay={0.2}
                    />
                    <StatCard
                      label="Pattern Insights"
                      value={parsed?.pattern_insights?.length ?? 0}
                      sub="Identified exam patterns"
                      delay={0.25}
                    />
                  </div>

                  {/* DISTRIBUTIONS */}
                  <SectionTitle>Distributions</SectionTitle>
                  <div className="dist-grid">
                    {/* Difficulty */}
                    {diffData?.length > 0 && (
                      <DistCard
                        icon="⚡"
                        title="Difficulty Distribution"
                        iconBg="rgba(251,191,36,0.1)"
                        animDelay="0s"
                      >
                        {diffData?.map((d, i) => (
                          <AnimatedBar
                            key={d.label}
                            label={d.label}
                            value={d.value}
                            color={
                              DIFF_COLORS[d.label.toLowerCase()] || "#4f7cff"
                            }
                            delay={i * 150}
                          />
                        ))}
                      </DistCard>
                    )}

                    {/* Cognitive */}
                    {cogData?.length > 0 && (
                      <DistCard
                        icon="🧠"
                        title="Cognitive Distribution"
                        iconBg="rgba(79,124,255,0.1)"
                        animDelay="0.1s"
                      >
                        <DonutChart data={cogData} colors={COG_COLORS} />
                      </DistCard>
                    )}

                    {/* Question Types */}
                    {bp?.question_type_distribution?.length > 0 && (
                      <DistCard
                        icon="❓"
                        title="Question Types"
                        iconBg="rgba(167,139,250,0.1)"
                        animDelay="0.2s"
                      >
                        {bp?.question_type_distribution?.map((q, i) => (
                          <AnimatedBar
                            key={q.type}
                            label={q.type.split(" ").slice(0, 2).join(" ")}
                            value={q.percentage}
                            color={Q_COLORS[i % Q_COLORS.length]}
                            delay={i * 120}
                          />
                        ))}
                      </DistCard>
                    )}

                    {/* Visual Types */}
                    {bp?.visual_distribution?.types?.length > 0 && (
                      <DistCard
                        icon="👁"
                        title="Visual Question Types"
                        iconBg="rgba(34,211,238,0.1)"
                        animDelay="0.3s"
                      >
                        <div className="visual-tags">
                          {bp?.visual_distribution?.types?.map((v, i) => (
                            <div className="visual-tag" key={i}>
                              <span className="visual-tag-icon">
                                {VISUAL_ICONS[v.type] || "📌"}
                              </span>
                              <div className="visual-tag-info">
                                <span className="visual-tag-name">
                                  {v?.type?.split("(")[0].trim()}
                                </span>
                                <span className="visual-tag-role">
                                  {v?.role}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </DistCard>
                    )}
                  </div>

                  {/* UNIT WEIGHTAGE */}
                  {pyq?.units?.length > 0 && (
                    <>
                      <SectionTitle>
                        Unit Weightage &amp; Intelligence
                      </SectionTitle>
                      <p
                        style={{
                          fontSize: 12,
                          color: "var(--text-muted)",
                          fontFamily: "'DM Mono',monospace",
                          marginBottom: 20,
                          letterSpacing: "0.04em",
                        }}
                      >
                        Click any unit to expand topics ↓
                      </p>
                      <div className="units-grid units-section">
                        {pyq?.units?.map((unit, i) => (
                          <UnitRow
                            key={unit._id || i}
                            unit={unit}
                            delay={i * 0.06}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {/* PATTERN INSIGHTS */}
                  {parsed?.pattern_insights?.length > 0 && (
                    <>
                      <SectionTitle>Pattern Intelligence</SectionTitle>
                      <div className="insights-grid">
                        {parsed?.pattern_insights?.map((insight, i) => (
                          <InsightCard key={i} text={insight} index={i} />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              examStream && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                    color: "var(--text-muted)",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 13,
                  }}
                >
                  No PYQ analysis data available for the selected stream.
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PyqAnalyticsDashboard;
